/**
 * Distribuição de sobra pelo método dos maiores restos.
 *
 * O problema, com o exemplo mais banal possível: R$ 10,00 para 3 pessoas.
 * 1000 / 3 = 333,33…, e três parcelas de 333 somam 999. Sobra 1 centavo, e ele
 * precisa ir para alguém.
 *
 * As alternativas e por que não:
 *
 * - **Arredondar cada parcela.** 334 × 3 = 1002. A soma deixa de bater com o
 *   total, que é o único invariante que não pode quebrar.
 * - **Jogar tudo na última pessoa.** Funciona, mas concentra a sobra sempre no
 *   mesmo lugar; com muitas divisões, uma pessoa acumula sistematicamente.
 * - **Maiores restos.** O centavo vai para quem ficou mais perto de merecê-lo,
 *   e o desempate é determinístico pela ordem de entrada. É o método usado em
 *   apuração proporcional de assentos, e é o que usamos aqui.
 *
 * Determinismo importa: a mesma despesa dividida no celular de uma pessoa e no
 * de outra precisa dar exatamente a mesma tabela, ou o grupo vê números
 * diferentes para a mesma conta.
 */

export interface RemainderShare {
  /** Parte inteira antes de distribuir a sobra. */
  base: number;
  /** Resto fracionário, em unidades da menor moeda, para ordenar. */
  remainder: number;
  index: number;
}

/**
 * Distribui `total` proporcionalmente a `weights`, em inteiros.
 *
 * Pesos podem ser quaisquer números não negativos: partes iguais são pesos
 * iguais, porcentagens são os próprios percentuais, divisão por família é o
 * tamanho de cada família.
 */
export function distributeByWeight(
  total: number,
  weights: readonly number[],
): number[] {
  if (weights.length === 0) return [];
  if (weights.some((w) => w < 0 || !Number.isFinite(w))) {
    throw new Error('Pesos precisam ser números não negativos.');
  }

  const somaPesos = weights.reduce((a, b) => a + b, 0);
  if (somaPesos === 0) {
    throw new Error('A soma dos pesos não pode ser zero.');
  }

  // Trabalha com o valor absoluto e devolve o sinal no fim: um estorno é um
  // total negativo, e "maior resto" precisa significar a mesma coisa nos dois.
  const sinal = total < 0 ? -1 : 1;
  const absoluto = Math.abs(total);

  const partes: RemainderShare[] = weights.map((peso, index) => {
    const exato = (absoluto * peso) / somaPesos;
    const base = Math.floor(exato);
    return { base, remainder: exato - base, index };
  });

  const distribuido = partes.reduce((soma, parte) => soma + parte.base, 0);
  let sobra = absoluto - distribuido;

  // Maior resto primeiro; empate pela ordem de entrada, que é estável.
  const ordenadas = [...partes].sort(
    (a, b) => b.remainder - a.remainder || a.index - b.index,
  );

  const resultado = partes.map((parte) => parte.base);
  for (const parte of ordenadas) {
    if (sobra <= 0) break;
    resultado[parte.index] = (resultado[parte.index] ?? 0) + 1;
    sobra -= 1;
  }

  return resultado.map((valor) => valor * sinal);
}
